import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    InputGroup,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Textarea, Collapse, FormHelperText} from '@chakra-ui/react'
import {GiftIcon, PlusIcon} from '../../../components/custom-icons'
import {useIntl, FormattedMessage} from 'react-intl'
import {useItemVariant} from '../../../components/item-variant'
import {SPECIAL_REGEX_1} from '../../../constants'
import {CustomCheckboxIcon} from '../../../components/field'

/**
 * A text area component for displaying and editing a materialized gift messages from variant.
 * @param   {number} index - The key of the gift message.
 * @param   {string[]} messages - The array of current gift messages.
 * @param   {Function} setMessages - The setter function for updating the gift messages.
 * @param   {Function} onMessagesChange - The function that fires when a message is deleted or blurred.
 * @returns {React.ReactElement} A react component that handles rendering a textarea for gift messages.
 */
const GiftTextarea = ({index, messages, setMessages, onMessagesChange}) => {
    const intl = useIntl()
    const currentMsg = messages[index]
    const [remainingCharacters, setRemainingCharacters] = useState(128 - currentMsg?.length)

    // Handles changes made to the textarea when typing within it.
    const handleInputChange = (value) => {
        if (SPECIAL_REGEX_1.test(value) || value === '') {
            // Split the input into lines
            const lines = value.split('\n')

            // Process each line to ensure no line exceeds 32 characters
            const processedLines = lines.map((line) => {
                let processedLine = ''
                for (let i = 0; i < line.length; i += 32) {
                    processedLine += line.slice(i, i + 32)
                    if (i + 32 < line.length) {
                        processedLine += '\n' // Insert newline if the line exceeds 32 characters
                    }
                }
                return processedLine
            })

            // Join the processed lines back into a single string
            const wrappedValue = processedLines.join('\n')

            // Check if the total lines are within 4
            const totalLines = wrappedValue.split('\n').length
            if (totalLines <= 4) {
                const newMessages = [...messages]
                newMessages[index] = wrappedValue
                setMessages(newMessages)
                // Newline characters are counted too
                setRemainingCharacters(128 - wrappedValue.length)
            }
        }
    }

    // Save messages when user focus out a message textarea
    const handleInputBlur = (msg) => {
        if (msg.trim().length > 0 && onMessagesChange) {
            onMessagesChange()
        }
    }

    return (
        <FormControl variant={'floating'} mt={24}>
            <InputGroup>
                <Textarea
                    onChange={(e) => handleInputChange(e.target.value)}
                    onBlur={() => handleInputBlur(messages[index])}
                    value={currentMsg}
                    placeholder={intl.formatMessage({
                        id: 'cart_gift_checckbox.messages.textarea.placeholder',
                        defaultMessage: 'Enter gift message'
                    })}
                    rows={4}
                    maxLength={128} // 125 visible characters + 3 newlines
                />
            </InputGroup>
            <FormLabel>
                <FormattedMessage
                    defaultMessage="Gift Message (Optional)"
                    id="cart_gift_checckbox.messages.textarea.label"
                />
            </FormLabel>
            <FormHelperText>
                <Text variant={'bodySmall'} color="darkGray" mt={4}>
                    <FormattedMessage
                        defaultMessage="{remainingCharacters, plural, one {# character} other {# characters}} remaining of 128. Maximum 4 lines."
                        id="cart_gift_checckbox.messages.textarea.helper_text"
                        values={{
                            remainingCharacters: remainingCharacters
                        }}
                    />
                </Text>
            </FormHelperText>
        </FormControl>
    )
}

/**
 * A cart tertiary button group component for rendering gift checkbox.
 * @param   {GiftCheckbox.propTypes} GiftCheckboxProps
 * @param   {boolean}   GiftCheckboxProps.isAGift - Whether the item is a gift.
 * @param   {Function}  GiftCheckboxProps.onIsAGiftChange - A function that's called when the "is a gift?" value for the variant is changed.
 * @returns {React.ReactElement} A react component that handles rendering gift checkbox.
 */
const GiftCheckbox = ({isAGift, onIsAGiftChange, onMessagesChange}) => {
    const intl = useIntl()
    const addButton = useRef()
    const [wasJustRemoved, setWasJustRemoved] = useState(false)
    const variant = useItemVariant()
    const initialMessages = variant?.c_messages ?? ['']
    const giftMessagesRef = useRef()
    const [messages, setMessages] = useState(initialMessages)
    const [openStates, setOpenStates] = useState(initialMessages.map(() => true)) // Track open state for each textarea

    /**
     * Adds a new message to the list of gift messages and updates the UI accordingly.
     *
     * This function updates the state by appending an empty string to the list of messages,
     * indicating a new message entry. It also ensures that the newly added message textarea
     * is opened and focused for user input. The focus operation is delayed using `setTimeout`
     * to ensure the DOM is updated before attempting to focus the new textarea.
     *
     * Side Effects:
     * - Updates the `messages` state with a new empty message.
     * - Updates the `openStates` state to open the newly added textarea.
     * - Focuses the newly added textarea for immediate user input.
     *
     * Note:
     * - The function assumes that `giftMessagesRef` is a reference to a DOM element containing
     *   the textareas, and it uses this reference to access and focus the last textarea.
     */
    const addNewMessage = () => {
        setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages, '']
            setOpenStates([...openStates, true]) // Open the newly added textarea
            // Give focus to the last textarea added
            setTimeout(() => {
                const textareas = giftMessagesRef.current.getElementsByTagName('textarea')
                if (textareas.length > 0) {
                    textareas[textareas.length - 1].focus()
                }
            }, 0)
            return updatedMessages
        })
    }

    /**
     * Removes a message from the list of messages at the specified index and updates the state accordingly.
     *
     * This function updates the list of messages by filtering out the message at the given index. It also
     * updates the open states by removing the corresponding state entry. After updating the messages and
     * open states, it triggers the `onMessagesChange` callback to save the changes into the basket.
     *
     * @param {number} index - The index of the message to be removed from the list.
     */
    const removeMessage = (index) => {
        setMessages((prevMessages) => {
            const updatedMessages = prevMessages.filter((_, i) => i !== index)
            setOpenStates(openStates.filter((_, i) => i !== index - 1)) // Update open states
            onMessagesChange(variant, updatedMessages) // Save changes into basket
            return updatedMessages
        })
        setWasJustRemoved(true)
    }

    // Ensure the number of messages does not exceed the variant quantity
    // (after decreasing quantity for example)
    useEffect(() => {
        if (messages.length > variant?.quantity) {
            setMessages((prevMessages) => {
                return prevMessages.slice(0, variant?.quantity)
            })
            setOpenStates((prevOpenStates) => {
                return prevOpenStates.slice(0, variant?.quantity)
            })
        } else if (wasJustRemoved) {
            addButton?.current?.focus()
            setWasJustRemoved(false)
        }
    }, [variant?.quantity, messages.length])

    return (
        <Flex direction="column" bgColor={'lightBeige'} mt={16} w="full" p={'8px 16px'}>
            <Flex justifyContent="space-between" w="full">
                <Checkbox
                    icon={<CustomCheckboxIcon />}
                    iconSize="0.1rem"
                    name={`gift-checkbox-${variant?.itemId}`}
                    spacing={8}
                    isChecked={isAGift}
                    onChange={(e) => {
                        const checked = e.target.checked
                        onIsAGiftChange(variant, checked)
                    }}
                >
                    <FormattedMessage
                        defaultMessage="Deliver in a FREE gift box"
                        id="cart_gift_checkbox.label.this_is_gift"
                    />
                </Checkbox>
                <GiftIcon boxSize="24px" />
            </Flex>
            <Box hidden={!isAGift} pt="20px" ref={giftMessagesRef}>
                <Text variant="bodyBase1">
                    <FormattedMessage
                        defaultMessage="Would you like to add a personal message?"
                        id="cart_gift_checkbox..label.personal_message"
                    />
                </Text>
                <Text variant={'bodySmall'} mt="8px">
                    <FormattedMessage
                        defaultMessage="Your personalized message will be printed on a card and placed in the gift box. A personalized message can be added for each item."
                        id="cart_gift_checckbox.label.personalized_message"
                    />
                </Text>
                <GiftTextarea
                    index={0}
                    messages={messages}
                    setMessages={setMessages}
                    onMessagesChange={() => {
                        onMessagesChange(variant, messages)
                    }}
                />
                {/* Additional messages in collapse */}
                {messages.slice(1).map((msg, index) => (
                    <Box mx={-2} key={index + 1}>
                        <Collapse in={openStates[index + 1]} animateOpacity>
                            <Box borderTopWidth="1px" borderColor="borderGray" mt="16px" px={2}>
                                <Box textAlign={'right'}>
                                    <Button
                                        variant="link"
                                        onClick={() => removeMessage(index + 1)}
                                        mt={16}
                                    >
                                        {intl.formatMessage({
                                            defaultMessage: 'Remove',
                                            id: 'cart_gift_checckbox.button.remove_gift_message'
                                        })}
                                    </Button>
                                </Box>
                                <GiftTextarea
                                    index={index + 1}
                                    messages={messages}
                                    setMessages={setMessages}
                                    onMessagesChange={() => {
                                        onMessagesChange(variant, messages)
                                    }}
                                />
                            </Box>
                        </Collapse>
                    </Box>
                ))}

                <Button
                    onClick={addNewMessage}
                    w="full"
                    px={0}
                    borderTopWidth="1px"
                    borderColor="borderGray"
                    mt={16}
                    justifyContent={'flex-start'}
                    ref={addButton}
                    display={messages.length < variant?.quantity ? 'inline-flex' : 'none'}
                    whiteSpace={'wrap'}
                    textAlign={'left'}
                >
                    <PlusIcon boxSize={'12px'} mr={4} />
                    <FormattedMessage
                        defaultMessage="Would you like to add a personalized message for the item {nextItemNumber}?"
                        id="cart_gift_checckbox.button.add_more_gift_message"
                        values={{nextItemNumber: messages.length + 1}}
                    />
                </Button>
            </Box>
        </Flex>
    )
}

GiftCheckbox.propTypes = {
    isAGift: PropTypes.bool,
    onIsAGiftChange: PropTypes.func,
    onMessagesChange: PropTypes.func
}

GiftTextarea.propTypes = {
    index: PropTypes.number,
    messages: PropTypes.arrayOf(PropTypes.string),
    setMessages: PropTypes.func,
    onMessagesChange: PropTypes.func
}

export default GiftCheckbox
